import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteAdminUser, type AdminUser } from "@/queries/adminUsers";

import { TypeToConfirm, matchesExpected } from "./TypeToConfirm";
import { classifyApiError } from "./errors";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser;
  onDeleted?: () => void;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  onDeleted,
}: DeleteUserDialogProps) {
  const { t } = useTranslation("admin-users");
  const remove = useDeleteAdminUser(user.id);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setConfirmEmail("");
      setErrorKey(null);
      remove.reset();
    }
    onOpenChange(next);
  };

  const onConfirm = () => {
    setErrorKey(null);
    if (!matchesExpected(confirmEmail, user.email)) {
      setErrorKey("mismatch");
      return;
    }
    remove.mutate(undefined, {
      onSuccess: () => {
        handleOpenChange(false);
        onDeleted?.();
      },
      onError: (err) => {
        const bucket = classifyApiError(err);
        if (bucket === "self") setErrorKey("self");
        else if (bucket === "lastAdmin") setErrorKey("lastAdmin");
        else if (bucket === "hasDependents") setErrorKey("hasDependents");
        else setErrorKey("generic");
      },
    });
  };

  const canConfirm = matchesExpected(confirmEmail, user.email) && !remove.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("delete.title")}</DialogTitle>
          <DialogDescription>
            {t("delete.description", { email: user.email })}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <AlertDescription>{t("delete.warning")}</AlertDescription>
        </Alert>

        <TypeToConfirm
          expected={user.email}
          value={confirmEmail}
          onChange={setConfirmEmail}
          label={t("delete.typeToConfirmLabel")}
          help={t("delete.typeToConfirmHelp", { email: user.email })}
          autoFocus
          disabled={remove.isPending}
        />

        {errorKey ? (
          <Alert variant="destructive">
            <AlertDescription>{t(`delete.errors.${errorKey}`)}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={remove.isPending}
          >
            {t("delete.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {remove.isPending ? t("delete.submitting") : t("delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
